import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, RefreshCw } from "lucide-react";
import { useAgentMcpStatus, useAgentConnectors } from "@/api/queries/connectors";
import { SectionCard } from "@/components/SectionCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Agent, McpServerStatusView } from "@/types";

// #237 (UC-status): Live-MCP-Server-Status des Brains (was er beim nächsten Turn lädt).
// managed = von uns verwalteter Connector (Name == connectorId); manuell = aus .mcp.json/
// Plugins (read-only). Manuell: „Verbinden" sichtbar aber disabled (kommt evtl. später).
type StatusValue = McpServerStatusView["status"];

const STATUS_BADGE: Record<StatusValue, "active" | "destructive" | "busy" | "offline" | "disabled"> = {
  connected: "active",
  failed: "destructive",
  "needs-auth": "busy",
  pending: "offline",
  disabled: "disabled",
};

export function McpStatusSection({ agent }: { agent: Agent }) {
  const { data, refetch, isFetching, error, isFetched } = useAgentMcpStatus(agent.id);
  const connectors = useAgentConnectors(agent.id);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [search, setSearch] = useState<string>("");

  // managed = der Server-Name matcht eine connectorId (unsere Connectors heißen = ihre id).
  const managedById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of connectors.data ?? []) m.set(c.connectorId, c.name);
    return m;
  }, [connectors.data]);

  const rows = useMemo(() => {
    const servers = data?.servers ?? [];
    return servers
      .map((s) => ({
        server: s,
        managed: managedById.has(s.name),
        displayName: managedById.get(s.name) ?? s.name,
      }))
      .filter((r) => statusFilter === "all" || r.server.status === statusFilter)
      .filter((r) => sourceFilter === "all" || (sourceFilter === "managed" ? r.managed : !r.managed))
      .filter((r) => search.trim() === "" || r.displayName.toLowerCase().includes(search.trim().toLowerCase()));
  }, [data, managedById, statusFilter, sourceFilter, search]);

  return (
    <SectionCard
      icon={<Activity className="h-[18px] w-[18px]" />}
      title="MCP-Status (live)"
      description="Was der Brain tatsächlich lädt — managed Connectors + manuelle (.mcp.json/Plugins)."
      action={
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} data-testid="mcp-status-refresh">
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> {isFetching ? "Frage Brain …" : "Live-Status abrufen"}
        </Button>
      }
    >
      <div className="space-y-3">
        {!isFetched && !isFetching && (
          <p className="text-[12.5px] text-text3">
            „Live-Status abrufen" startet im Brain eine kurze Abfrage (verbindet die MCP-Server) — daher manuell.
          </p>
        )}

        {error && <p className="text-[12.5px] text-danger" data-testid="mcp-status-error">Abruf fehlgeschlagen: {(error as Error).message}</p>}

        {isFetched && data && !data.brainOnline && (
          <p className="text-[12.5px] text-text3" data-testid="mcp-status-offline">
            ○ Kein Live-Status — der Brain ist offline oder antwortet nicht (kein Responder/Timeout).
          </p>
        )}

        {isFetched && data?.brainOnline && (
          <>
            {/* Filter */}
            <div className="flex flex-wrap gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="MCP suchen…"
                className="h-9 w-44"
                data-testid="mcp-filter-search"
              />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44" data-testid="mcp-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Status: alle</SelectItem>
                  <SelectItem value="connected">connected</SelectItem>
                  <SelectItem value="failed">failed</SelectItem>
                  <SelectItem value="needs-auth">needs-auth</SelectItem>
                  <SelectItem value="pending">pending</SelectItem>
                  <SelectItem value="disabled">disabled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-44" data-testid="mcp-filter-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Quelle: alle</SelectItem>
                  <SelectItem value="managed">managed</SelectItem>
                  <SelectItem value="manual">manuell</SelectItem>
                </SelectContent>
              </Select>
              <span className="ml-auto self-center text-[11.5px] text-text3">{rows.length} von {data.servers.length}</span>
            </div>

            {rows.length === 0 ? (
              <p className="text-[12.5px] text-text3">Keine MCP-Server (für diesen Filter).</p>
            ) : (
              <ul className="space-y-1.5" data-testid="mcp-status-list">
                {rows.map(({ server, managed, displayName }) => (
                  <li key={`${server.name}-${server.scope ?? ""}`} className="flex items-center gap-3 rounded-md border border-border px-2.5 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {managed ? (
                          <Link to={`/connectors/${server.name}`} className="truncate text-[13px] font-medium text-accent-text hover:underline">{displayName}</Link>
                        ) : (
                          <span className="truncate text-[13px] font-medium">{displayName}</span>
                        )}
                        <Badge variant={managed ? "enrolled" : "default"}>{managed ? "managed" : "manuell"}</Badge>
                        {server.scope && <span className="mono text-[10.5px] text-text3">{server.scope}</span>}
                      </div>
                      {server.error && <div className="mt-0.5 truncate text-[11px] text-danger" title={server.error}>{server.error}</div>}
                      {server.tools.length > 0 && <div className="mt-0.5 text-[11px] text-text3">{server.tools.length} Tools: {server.tools.slice(0, 6).join(", ")}{server.tools.length > 6 ? " …" : ""}</div>}
                    </div>
                    <Badge variant={STATUS_BADGE[server.status]}>{server.status}</Badge>
                    {!managed && (
                      <Button variant="outline" size="sm" disabled title="Manuelle MCPs (aus .mcp.json/Plugins) sind read-only — Verbinden folgt evtl. später">
                        Verbinden
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}
